import { Link } from 'react-router-dom'
import { ArrowLeft, BotMessageSquare, Music, Volume2, Mic, Guitar } from 'lucide-react'
import { TextToMusicGallery } from '@/components/gallery/TextToMusicGallery'
import { MusicGalleryItem } from '@/models/gallery'
import * as stylex from '@stylexjs/stylex'
import { musicStyles as styles } from '@/styles/MusicPage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'Suno Music',
    shortName: 'Suno',
    modelKey: 'suno-music',
    creditsPerRequest: 6,
    providerUrl: 'https://suno.ai/',
    providerName: 'Suno AI',
    pageUrl: 'https://docs.aibrush.co/music-models/suno',
} as const;

const textToMusicGalleryItems: MusicGalleryItem[] = [
    {
        id: "suno-music-1",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-1.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-2.mp3",
        title: "The Island of Glass",
        mode: "song",
        vocalGender: "male",
        prompt: `[Verse]
Out on the waves where the winds run fast
Lies an isle of wonder made of glass
It gleams and it glows in the morning sun
A brittle jewel where no man’s begun

[Chorus]
Oh row lads row to the island of glass
Where the sea and the sky let the sunlight pass
Oh row lads row let the oars beat true
For the glass it shines and it cuts straight through

[Verse 2]
The seagulls cry but they don’t dare land
On a shore so sharp it could slice a hand
The trees they shimmer like crystal towers
Yet crumble like dreams in the midnight hours

[Chorus]
Oh row lads row to the island of glass
Where the sea and the sky let the sunlight pass
Oh row lads row let the oars beat true
For the glass it shines and it cuts straight through

[Bridge]
The moonlight dances on fractured waves
The island hums with the songs of graves
Beware the pull of its gleaming glow
For its beauty hides what you'll never know

[Chorus]
Oh row lads row to the island of glass
Where the sea and the sky let the sunlight pass
Oh row lads row let the oars beat true
For the glass it shines and it cuts straight through`,
        style: "Upbeat and lively Celtic/Irish folk instrumental, featuring a prominent and energetic fiddle melody, supported by driving acoustic guitar, steady bass, and spirited percussion. The instrumentation also includes fast-paced plucked strings reminiscent of mandolin or banjo, creating a cheerful, traditional, and danceable atmosphere.",
    },
    {
        id: "suno-music-2",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-3.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-4.mp3",
        title: "The Unsolved Crime",
        mode: "song",
        vocalGender: "male",
        prompt: `[Verse 1 - Country]
The sheriff found her tracks in the clay
A blood-red moon
That cursed day
Her hat left hanging on the pine
A shadow lost
No trace
No sign

[Chorus - Country]
Who done it? No one knows
A mystery deep where the cold wind blows
She vanished
Left no name
The town still whispers of the flame

[Verse 2 - Heavy Metal]
Blood on the ground
Echoes in the night
A scream that fades
A desperate fight!
Bones in the dirt
Secrets in the dark
A soul devoured
No light
No spark!

[Chorus - Heavy Metal]
Who took her life? Who stole her breath?
A phantom's hand delivering death!
No answers found
No justice served
Her cry forgotten
Her truth unnerved!

[Bridge - Jazz]
Smoke curls up
A cigarette glow
They whisper in bars
But they’ll never know
The saxophone weeps for the crime untold
A melody haunting
A chill so cold

[Chorus - Jazz]
Who took her away
Into the mist?
A kiss of death
A clenched iron fist
We drown the truth in a glass of rye
While the notes fade
We question why`,
        style: "A powerful fusion of atmospheric rock and melodic metal. It opens with a soulful, saxophone-like synth lead, creating a cinematic mood before launching into aggressive, palm-muted rhythm guitar riffs. A virtuosic lead guitar delivers a searing, melodic solo. The track is propelled by a powerhouse rhythm section, featuring precise, energetic drumming with heavy double-bass and a thick, anchoring bass guitar that provides a massive low-end foundation.",
    },
    {
        id: "suno-music-3",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-5.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-6.mp3",
        title: "Hollow in My Chest",
        mode: "song",
        vocalGender: "male",
        prompt: `(Verse 1)
Steel whispers in the sterile air, a hum of forgotten souls,
Each glowing fragment, a life unburdened, filling endless roles.
I trace the spectral data, a phantom limb of truth,
Another past discarded, a fragment of my youth.

(Pre-Chorus)
And the silence here, it screams of what I've done,
Every fleeting image, a battle lost, never won.

(Chorus)
There's a void where laughter used to bloom, a silence deep inside,
A fractured melody of self, where shadows now reside.
I sought solace in the sell-off, a quiet, empty mind,
But the echoes of a buried past, in this archive, I now find.
Miserable and adrift, a ghost in this stark, metallic shell,
Where does the archivist end, and the forgotten memories swell?

(Verse 2)
They promised sweet oblivion, a clean and fresh start anew,
But the phantom pains remain, scars of what I've been through.
This dull ache in my core, a constant, chilling dread,
For a life I willingly abandoned, words I left unsaid.

(Bridge)
Each flicker, a phantom touch, a whisper in the gloom,
A fragment of a smile, a taste of joy, now sealed within this tomb.
I try to grasp the edges, but they slip like smoke through hands,
A history I traded, for a future made of shifting sands.

(Verse 3)
I walk these endless aisles, a prisoner of my own design,
Each stored emotion, a ghost of what was once truly mine.
Did I truly believe relief could be bought and sold?
Now only emptiness remains, a story left untold.

(Chorus)
There's a void where laughter used to bloom, a silence deep inside,
A fractured melody of self, where shadows now reside.
I sought solace in the sell-off, a quiet, empty mind,
But the echoes of a buried past, in this archive, I now find.
Miserable and adrift, a ghost in this stark, metallic shell,
Where does the archivist end, and the forgotten memories swell?

(Outro)
The hum continues... a silent scream,
Trapped within this digital dream.
My own echoes, a haunting light,
Lost in the archive, endless night.`,
        style: "Deeply melancholic and introspective electronic ambient music with pronounced industrial undertones, evoking a crushing sense of oppressive solitude and existential dread. Features include: Atmosphere: Expansive, cold, and distant synth pads layered with a continuous, low-frequency drone, creating an overwhelming, empty sonic space. Intermittent, subtle metallic clangs and digital whirs add to the sterile, dystopian environment. Rhythm: A slow, irregular, and almost imperceptible pulse or heartbeat, deliberately glitchy and distant, utilizing non-traditional percussive elements like faint data processing sounds or grinding metal. This evokes a sense of internal struggle and a failing connection to reality. Melody: Sparse, haunting, and dissonant piano or cello motifs in a minor key, played with heavy reverb and delay, emphasizing profound loss, isolation, and regret. The melodies are fragmented, mirroring fractured memories. Texture: Rich incorporation of sharp metallic sound effects",
    },
    {
        id: "suno-music-4",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-7.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-8.mp3",
        title: "what a f*cking joke",
        mode: "song",
        vocalGender: "female",
        prompt: `Haha, what a f*cking joke...

I can't brush this crap off
I'm gonna make it clear
Don't screw with this chick
Folks act like it's fine
To act all out of line
They pull it off once
Figure they can try it twice
Then hit it three rounds
And by that third go
You start feeling nuts
'Cause now it's a habit
It's straight-up mind games
And that's why I won't drop this crap
Because I won't let you believe
That this crap is okay
You might slide by for a bit
But payback's always spot on
She's got my back
And she's my ally
Because what spins out circles back
It's called payback
(Darlin')
You can chant it with me
It's called payback
(Darlin')
Say it once more
(Darlin')
It's called payback, darlin'
(Darlin')
Chant it with me
What spins out circles back
Your clock's run out
I've arrived to collect on your wrongs
It's past the point now
You can't dodge the mess you're in
This is the fallout
I don't get why you're shocked
You harvest what you plant
So yeah, you're bound to flop
You're bawling on the ground while I'm watching with a grin
You could've picked me
But you opted to pick
To mess me over
So
How'd you figure this would wrap?
I was your lady
Thought we were tight
You tossed it for some quick thrill
But
Darlin'
That's your reward
Because what spins out circles back
It's called payback
(Darlin')
You can chant it with me
It's called payback
(Darlin')
Say it once more
(Darlin')
It's called payback, darlin'
(Darlin')
Chant it with me
What spins out circles back
You figured you could mess me over and slip away
The facts are closing in on you
And you can't handle it
I handed you way too many shots
But you blew them all
You built your trap
So now you gotta stay in it
I know you're regretting
But I'm regretting too
For believing in you
It's way too late now
No fix you can pull
That's your reward
Because what spins out circles back
It's called payback
(Darlin')
You can chant it with me
It's called payback
(Darlin')
Say it once more
(Darlin')
It's called payback, darlin'
(Darlin')
Chant it with me
What spins out circles back`,
        style: "This track is modern electronic pop, blending cinematic atmospheres with trap and R&B elements. It opens with ethereal synth pads and a haunting, processed vocal melody before the beat drops. The rhythm is driven by a deep, rumbling 808 sub-bass, a crisp electronic snare, and intricate, rapid-fire hi-hats. The arrangement builds dramatically in the chorus, layering epic, cinematic synth strings and soaring lead lines to create a dense, powerful wall of sound that contrasts with the sparser verses.",
    },
    {
        id: "suno-music-5",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-9.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-10.mp3",
        title: "Ain't Got a Dollar",
        mode: "song",
        vocalGender: "male",
        prompt: `(Verse 1)  
Woke up this mornin', feelin' the weight on my soul,  
Bills keep on comin', losin' control, yeah,  
Worked through the fire, but the money's all gone,  
No light in the tunnel, but I'm holdin' on strong.  

(Chorus)  
Ain't got a dollar, ain't got a dream to my name,  
Dancin' through the struggle, playin' the game,  
Gonna rise up higher, break through the flame,  
Ain't got a dollar, ain't got a dream to my name. Hee-hee!  

(Verse 2)  
Got hearts to nurture, and a home fallin' apart,  
Every step's a rhythm, straight from the heart,  
But I won't surrender, won't let the darkness win,  
Gonna moonwalk forward, let the healin' begin.  

(Chorus)  
Ain't got a dollar, ain't got a dream to my name,  
Dancin' through the struggle, playin' the game,  
Gonna rise up higher, break through the flame,  
Ain't got a dollar, ain't got a dream to my name. Ow!  

(Bridge)  
In the midnight hour, when the world's feelin' cold,  
I'll keep on believin', turnin' pain into gold,  
Though the path is twisted, and the beat's gettin' long,  
I'll keep on singin', my unbreakable song. Shamone!  

(Chorus)  
Ain't got a dollar, ain't got a dream to my name,  
Dancin' through the struggle, playin' the game,  
Gonna rise up higher, break through the flame,  
Ain't got a dollar, ain't got a dream to my name.  

Outro  
(Instrumental fade with ad-libs: Hee-hee! Ow! Yeah!)`,
        style: `A high-energy, polished 80s dance-pop track with a strong funk and R&B foundation, set at 118 BPM.
The rhythm section is key: use a punchy, tight drum machine beat (LinnDrum style) layered with a live, driving four-on-the-floor kick and a crisp snare on the 2 and 4. The centerpiece is a melodic, syncopated, and groovy synth bassline that acts as a lead instrument.
Add clean, rhythmic electric guitar playing short, percussive funk riffs and muted "chicka-wah" patterns. Layer glistening digital synthesizers, including bright brassy stabs, lush atmospheric pads, and bell-like tones. Finally, incorporate a powerful, live horn section for dramatic stabs and swells to build energy in the chorus. The overall production must be pristine, spacious, and irresistibly danceable.`,
    },
    {
        id: "suno-music-6",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-11.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-12.mp3",
        title: "Dance Party",
        mode: "song",
        vocalGender: "male",
        prompt: `[Chorus]
Dancin' all night long
(Ooh-yeah!)
Dance party
All night long
(Ooh-yeah!)

[Verse]
It's a dance party
We'll be dancing all night
Dance party
We'll be dancing all night
(Ooh-yeah!)

[Pre-Chorus]
You know we're gonna
You know we're gonna
(Ooh-yeah!)

[Chorus]
Dance party
All night long
Dance party
All night long
(Ooh-yeah!)

[Verse 2]
It's a dance party
We'll be dancing all night
Dance party
We'll be dancing all night
(Ooh-yeah!)

[Pre-Chorus]
You know we're gonna
You know we're gonna
(Ooh-yeah!)`,
        style: `An energetic fusion of Middle Eastern scales with modern dance beats, featuring pulsing synth bass, layered percussion including doumbek and handclaps, and bright melodic hooks on oud and flute. The breakdown spotlights hypnotic drum patterns before a vibrant, celebratory climax.`,
    },
    {
        id: "suno-music-7",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-13.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-14.mp3",
        title: "A Cappella",
        mode: "song",
        vocalGender: "female",
        prompt: `[Verse]
Will it be Bach I sing?
Or maybe Bacharach?
Perhaps a fugue or jazz?
Can you imagine that?

[Pre-Chorus]
No matter how I sing

[Chorus]
It's still a cappella
It's still a cappella
It's still a cappella
Oh, a cappella`,
        style: `An a cappella arrangement fuses Brazilian bossa nova rhythms with jazz harmonies. Syncopated vocal percussion mimics brushed drums, while layered vocals recreate warm guitar chords, subtle basslines, and complex jazz chords. Scat-style solos add improvisational flair in instrumental breaks.`,
    },
    {
        id: "suno-music-8",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-15.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-16.mp3",
        title: "ネットセット",
        mode: "song",
        vocalGender: "none",
        prompt: `[Intro]
ああ
ああ

[Verse 1]
画面越しのビジョン 繋がるセッション
指先で描く 夜のコネクション
笑顔交わすマジック エンドレスのトラフィック
アイフォン越しのドラマ 心のファブリック

[Chorus]
手の中に映るは遠くのキミの面影
触れられない距離も 心は縮めてゲームセット
単独の夜も 孤独を超えてネットセット
繋がりはブレスレット 心の安

[Outro]
心の安`,
        style: `The J Hip Hop track rides a bold, head-nodding groove at 90 BPM with a punchy, syncopated bassline and sharp electronic drums. Verses layer crisp hi-hats and atmospheric synth accents, while the chorus expands with sampled strings and additional vocal harmonies for impact.`,
    },
    {
        id: "suno-music-9",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-17.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-18.mp3",
        title: "Girl Who Made a Wish",
        mode: "song",
        vocalGender: "female",
        prompt: `[Verse]
Once upon a time there was a girl who made a wish
To find herself a lovin' finally make a switch
Then you came around you healed another stitch
And I'm glad about that I can finally make a switch

[Pre-Chorus]
And I know yeah I know
Many would like to be in my shoes
And I know yeah I know
With you I got nothing to lose

[Chorus]
Oh woh
Do you want to be my man?
Oh woh
I don't understand
I don't know how
How they don't understand
Oh woh
Do you want to be my man?
Oh woh
I don't understand
I don't know how
How they don't understand

[Verse 2]
Wish that you could be in my shoes
Many people wanna be in my shoes
But they don't understand
Wish that you could be in my shoes
Many people wanna be in my shoes
But they don't understand

[Pre-Chorus]
And I know yeah I know
Many would like to be in my shoes
And I know yeah I know
With you I got nothing to lose

[Chorus]
Oh woh
Do you want to be my man?
Oh woh
I don't understand
I don't know how
How they don't understand
Oh woh
Do you want to be my man?
Oh woh
I don't understand
I don't know how
How they don't understand`,
        style: `This track weaves alternative R&B grooves with lush art pop layers: intricate synths, deep bass, syncopated drums, and effects-laden guitars converge. Verses flow with unpredictable chords, and a quirky, distinctive vocal leads. Psychedelic, progressive bridge morphs textures before a dreamy outro.`,
    },
    {
        id: "suno-music-10",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-19.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-20.mp3",
        title: "Digital Daze",
        mode: "song",
        vocalGender: "none",
        prompt: `[Verse 1 - Pop]
Swipe right swipe left where's the spark
Every screen feels cold and dark
Blue light nights and endless scroll
Where did we lose control

[Prechorus - Pop]
Notifications ring like bells of doom
A digital jungle we built this room

[Chorus - Pop]
Lost in the daze of a pixel maze
Living in codes counting the days
Hey hey can you hear me say
I'm drowning in a digital daze

[Verse 2 - Rap]
Glitches in my mind like a broken stream
Clouds in the sky full of shattered dreams
Wi-Fi weak but the pressure's strong
Keep it 5G but it feels so wrong

Algorithms watching tracking my soul
Selling my data just to fill their bowl
Swipe swipe type type life's on repeat
Tech got us locked like we're stuck in concrete

[Bridge - Opera]
Ohhh! My heart cries out through tangled wires
A symphony of lost desires
Machines hum loud
They steal my voice
Oh fate
Oh fate
Did I have a choice
`,
        style: `pulsing basslines for edm, male and female vocal interplay throughout, a heavy beat and flow for rap, rap, pop, a fusion of pop, and edm, each section transitions smoothly with distinct instrumentation: upbeat synths for pop, edm, opera, grand orchestration for opera`,
    },
    {
        id: "suno-music-11",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-21.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-22.mp3",
        title: "Clash of the Minds",
        mode: "song",
        vocalGender: "male",
        prompt: `[Verse 1: Newton]
Yo, I’m the OG, the apple that fell,
Gravity’s prophet, the king of intel.
Laws of motion, I wrote the creed,
You’re bending spacetime? I plant the seed.
Calculus carved by my own two hands,
You just expanded the math I planned.
Relativity? Nah, that’s cute, son,
But in this battle, I’m the shining sun.

[Verse 2: Einstein]
E=MC squared, the mic’s in my clutch,
Warping your laws, you’re trying too much.
Your apple fell? That’s a petty start,
I bend the cosmos, I tear them apart.
Newtonian physics? They’re kindergarten,
I’m quantum mechanics, the ultimate pardon.
You’re stuck in the past, your laws outdated,
In the annals of science, I’m celebrated.

[Chorus]
Brains collide, two legends spar,
From the apple to the farthest star.
Space and time, they clash and grind,
Newton and Einstein, a battle of minds.

[Verse 3: Newton]
Your theories crumble where mine hold true,
I paved the road, you just walked it through.
Your speed of light? A cosmic bluff,
Try measuring force, you ain’t tough enough.
I’m the architect, the universe’s spine,
You’re just the poet, reciting the line.
Respect your elder, the master is me,
You’re lost in equations; I’m pure clarity.

[Verse 4: Einstein]
Respect? That’s rich from a man so blind,
I see the curvature, you’re stuck confined.
You built the base, I’ll give you that,
But I’m the skyscraper, soaring where you’re flat.
Black holes, time dilation, I see the whole,
Your clockwork cosmos? I swallow it whole.
The speed of light’s the ultimate decree,
In this rap, Newton, you can’t outrun me.

[Bridge]
Newton's apple vs Einstein's beam,
Two titans clashing in a cosmic dream.
Whose mind prevails? The jury's`,
        style: `hard-hitting, gritty beats with orchestral strings and a booming 808 bassline, rap`,
    },
    {
        id: "suno-music-12",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-23.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-24.mp3",
        title: "Mon Ami Parti",
        mode: "song",
        vocalGender: "none",
        prompt: `[Verse]
Sous les étoiles qui ne brillent plus
Je cherche ton ombre dans la rue
Les murs murmurent mais toi t’es muet

[Prechorus]
Ton rire s’est envolé
Comme un avion dans le ciel

[Chorus]
Mon ami parti mon cœur brisé
Pourquoi la guerre t’a emporté
Les jours sont longs les nuits glacées
Mon ami parti t’es où caché

[Verse 2]
Tes pas résonnent dans ma mémoire
Chaque éclat de rire chaque victoire
Mais la bataille t’a volé trop tôt

[Prechorus]
Ton sourire est un fantôme
Qui danse au bord de mon royaume

[Chorus]
Mon ami parti mon cœur brisé
Pourquoi la guerre t’a emporté
Les jours sont longs les nuits glacées
Mon ami parti t’es où caché`,
        style: `french pop, emotive, piano-driven with a soft electronic beat, pop`,
    },
    {
        id: "suno-music-13",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-25.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-26.mp3",
        title: "Get Up and Try Again",
        mode: "song",
        vocalGender: "none",
        prompt: `[Verse]
Oh
I fall like potato
Hit da ground (oo-yeah!)
Face in dirt
But I don’t stay down (uh-huh!)
Life say
“Nyet
” but I say
“Da!”
Dust off my jacket
I go so far (woo!)

[Chorus]
Get up
Get up
Try again (get up!)
Fall seven times
Rise like ten (oo-yeah!)
Shake it off
Shake it off
Don’t cry
My friend (no-no!)
Life is dance
So spin and bend!

[Verse 2]
They laugh
They point
They call me fool (ha-ha!)
But I’m tough like bear
I’m breaking rule (grr!)
Turn my stumble into funky groove
Fail is step
I’m making moves!

[Prechorus]
Oh
You feel that beat inside? (inside!)
Failure just a funky ride!

[Chorus]
Get up
Get up
Try again (let’s go!)
Fall seven times
Rise like ten (come on!)
Shake it off
Shake it off
Don’t cry
My friend (uh-huh!)
Life is dance
So spin and bend!

[Bridge]
You say
“Why me?” I say
“Why not?” (why not?)
Life is stew
You stir the pot! (stir it up!)
Spicy
Sweet
Sour too
But you keep cooking
That’s what you do!`,
        style: `male vocals, syncopated guitar riffs, groovy bassline, vibrant horns, funk`,
    },
    {
        id: "suno-music-14",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-27.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-28.mp3",
        title: "Never Got To Say It",
        mode: "song",
        vocalGender: "male",
        prompt: `[Verse]
She wore the moonlight like a crown
Her laugh could turn the world around
I swear I heard angels in her sigh

[Prechorus]
But time’s a thief it never waits
I blinked and she was gone away

[Chorus]
I never got to say it girl
Never told you you were my world
Now the silence cuts me like a blade
I never got to say it girl
Now my heart's an empty pearl
Wishing for the words I never gave

[Verse 2]
She danced like shadows on the sea
Her eyes held secrets meant for me
But I stayed quiet played the fool

[Prechorus]
The clock ticked fast the night turned cold
Now I’ll never see her grow old

[Chorus]
I never got to say it girl
Never told you you were my world
Now the silence cuts me like a blade
I never got to say it girl
Now my heart's an empty pearl
Wishing for the words I never gave`,
        style: `Electric piano, and a steady drum machine beat. male vocals., smooth and soulful with lush synth pads, deep bass grooves, r&b, 80's style`,
    },
    {
        id: "suno-music-15",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/suno/sample-29.mp3",
        audioSrc2: "https://cdn.aibrush.co/docs/audio/models/suno/sample-30.mp3",
        title: "Polish Whispers",
        mode: "song",
        vocalGender: "female",
        prompt: `[Verse 1]

Here, I'll file those rough spots now

Buff 'em smooth, 'cause life's got edges somehow

Was your week all chipped and cracked after all?

I'll fix it up, any more, gossip after all
[Pre-Chorus]

Dipped in colors wild and bright

And I paint most of it

Most of it, with flair

Just to make you laugh
[Chorus]

I've been cracking jokes in every polish line

Trying to nail the punchline by the fireside

Trying to therapist your stress in the design

How we gonna keep it funny through the night?

Hand me your paws, I'll be your comic bind
[Verse 2]

There, I'll shape those cuticles now

Clip the drama, 'cause the stories are flowing now

Was it all any more dull after all?

I sparkle it up, any more, hilarious after all
[Pre-Chorus]

Layered with glitter and glue

And I spill most of it

Most of it, unwind

Just to tickle your soul
[Chorus]

I've been cracking jokes in every polish line

Trying to nail the punchline by the fireside

Trying to therapist your stress in the design

How we gonna keep it funny through the night?

Hand me your paws, I'll be your comic bind
[Bridge]

Faded base, but the top coat's tight

Whispers of bad puns under salon light

No more frowns, no more bad vibes stay

We'll stay nailed down, laughing all the way
[Outro]

I've been cracking... in every polish line

(Hand me your paws, I'll be yours...)`,
        style: `indie folk genre, characterized by ethereal acoustic arrangements, gentle falsetto vocals, ambient production, and introspective lyrics.`,
    },
];

export default function SunoMusicPage() {
    return (
        <div {...stylex.props(styles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(styles.heroSection, styles.musicHeroAnimated)}>
                <div {...stylex.props(styles.heroContent)}>
                    <h2 {...stylex.props(styles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <p {...stylex.props(styles.heroDescription)}>
                        Experience revolutionary <strong>Text to Music</strong> generation.
                        Create <strong>original compositions</strong> from simple text descriptions, featuring <strong>multiple genres</strong> and <strong>vocal options</strong>.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(styles.heroStats)}>
                        <div {...stylex.props(styles.stat)}>
                            <Music size={20} {...stylex.props(styles.statSvg)} />
                            <span>Text to Music</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Mic size={20} {...stylex.props(styles.statSvg)} />
                            <span>Vocal & Instrumental</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Guitar size={20} {...stylex.props(styles.statSvg)} />
                            <span>Multiple Genres</span>
                        </div>
                    </div>
                    <div {...stylex.props(styles.creditCostBadge)}>{MODEL_CONFIG.creditsPerRequest} credits per request</div>

                    {/* Featured Music Players */}
                    <div {...stylex.props(styles.heroVoicePlayer)}>
                        <audio controls {...stylex.props(styles.heroAudioPlayer)}>
                            <source src="https://cdn.aibrush.co/docs/audio/models/suno/sample-hero-song.mp3" type="audio/mpeg" />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                    <div {...stylex.props(styles.heroVoicePlayer)}>
                        <audio controls {...stylex.props(styles.heroAudioPlayer)}>
                            <source src="https://cdn.aibrush.co/docs/audio/models/suno/sample-hero-music.mp3" type="audio/mpeg" />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                </div>

                {/* Floating Icons Container */}
                <div {...stylex.props(styles.floatingIconsContainer)}>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon1)}>
                        <Music size={120} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon2)}>
                        <Mic size={80} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon3)}>
                        <Guitar size={90} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon4)}>
                        <Volume2 size={50} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                </div>
            </section>

            {/* AI Assistance Note */}
            <div {...stylex.props(styles.importantNote)}>
                <div {...stylex.props(styles.noteIcon)}>
                    <div {...stylex.props(styles.inlineChatButton)}>
                        <BotMessageSquare size={22} />
                    </div>
                </div>
                <div {...stylex.props(styles.noteText)}>
                    <strong>Need help with music descriptions or prompt writing?</strong> Click the
                    chat icon in the top-right corner of any component to get AI assistance with your music creation!
                </div>
            </div>

            {/* Music Gallery */}
            <TextToMusicGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerRequest} credits</strong> per request.`}
                title="Text to Music Gallery"
                description={`Explore the exceptional capabilities of <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.providerName}</a> through these AI-generated music samples. With advanced neural networks and natural language processing, Suno delivers high-quality, original music compositions. <strong>Click on any music sample</strong> to hear how it was created and see the parameters used.`}
                items={textToMusicGalleryItems} />
        </div>
    )
}